import { AutocompleteAPI, DateFieldAPI, FetchAPI, FilterAPI, InformationBarAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnClear, OnClick, OnCollapse, OnMount, OnSubmit, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClearEvent, ClickEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import moment from 'moment/moment.js';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import BaseControllerExams from '../BaseControllerExams';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { wrapImageBase64 } from '../../utils/wrapImageBase64';
import './exam-results.css';
import Audit from '../../app/audit';
import { Data } from '../../interfaces/data';
import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { Person } from '../../interfaces/Person';
import { isMobile } from '@philips-internal/oswald-ui-utils';

interface FilterList {
  filterExamType: FilterItem;
  filterExamStatus: FilterItem;
  filterExamRead: FilterItem;
  filterFromDate: string;
}

interface FilterItem {
  key: string;
  value: string;
}

interface FilterParams {
  [key: string]: string;
}

@Controller({ fragment: 'exam-results', component: 'layout-exams-result-list' })
class ExamResultsController extends BaseControllerExams {

  internalNavigation: boolean;

  @Inject('filter-exams-list')
  filterList: FilterAPI;

  @Inject('filter-field-exam-type')
  filterExamType: FilterAPI;

  @Inject('filter-field-exam-status')
  filterExamStatus: FilterAPI;

  @Inject('fetch-exams')
  fetchExams: FetchAPI;

  @Inject('date-picker-initial-date')
  datePickerInitialDate: DateFieldAPI;

  @Inject('information-bar')
  informationBar: InformationBarAPI;

  @Inject('autocomplete-type')
  autocompleteTypeList: AutocompleteAPI;

  @Inject('autocomplete-status')
  autocompleteStatusList: AutocompleteAPI;

  @Inject('autocomplete-read')
  autocompleteReadList: AutocompleteAPI;

  @OnCollapse({ target: 'information-bar' })
  onClickInformationBar(): void {
    const { informationBar } = this
    if (isMobile()) {
      return;
    }
    informationBar.setCollapsed(false);
  }

  @OnMount({ target: 'filter-exams-list' })
  OnFilterMout(event: MountEvent): void {
    const { informationBar, filterList } = this;
    const { patientId } = this.historyService.getCurrentRouteParams();
    this.internalNavigation = false;
    const filter = event.target as FilterAPI;

    HttpService.get(APPLICATION_ENDPOINTS.GET_DATA_PATIENT, { patientId: patientId }).then(response => {
      const dataPerson = response.body as Person;
      informationBar.setTitle(dataPerson.nmPessoaFisica);

      informationBar.setFieldOneTitle('result.profile.dob');
      informationBar.setFieldOneValue(moment(dataPerson.birthDatePessoaFisica).format('DD MMM YYYY'));

      if (!isMobile()) {
        informationBar.setCollapsed(false);
      }

      if (dataPerson.ieFotoPermTws == 'S' && dataPerson.imPessoaFisica) {
        informationBar.setImage(wrapImageBase64(dataPerson.imPessoaFisica, '/static/assets/icons/avatar-user-gray.svg'));
      } else {
        informationBar.setImage('/static/assets/icons/avatar-user-gray.svg');
      }
    });

    if (filter.restoreState(true)) {
      this.initialDate();
      const data = filter.getRecord() as FilterParams;
      if (data.filterFromDate) {
        data['filterFromDate'] = moment.utc(data.filterFromDate, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT);
      }
      filter.clear();
      filter.setData(data as Record<string, Value>);
      return;
    }

    HttpService.get(APPLICATION_ENDPOINTS.GET_EXAM_DATE_FROM_FILTER).then(({ body }) => {
      const filterFromDate: string = moment(body as string, ISO_DATE_FORMAT).toJSON();
      this.context.set({ initialValue: filterFromDate });

      const record = {
        filterFromDate: moment.utc(filterFromDate).format(ISO_DATE_FORMAT),
      };
      filterList.setData(record);
      filterList.submit();
    });
  }

  @OnUnmount('filter-exams-list')
  onUsersFilterUnmount(event: MountEvent): void {
    const filter = event.target as FilterAPI;
    if (this.internalNavigation) {
      filter.persistState();
    } else {
      filter.clearPersistedState();
    }
  }

  @OnUnmount({ target: 'date-picker-initial-date' })
  handleUnmountdatePickerInitial(): void {
    const dateNow = this.context.get('currentDate') as string;
    const formatDate = moment(dateNow, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT);
    const current = this.filterList.getRecord();
    this.filterList.setRecordMapper({ ...current, filterFromDate: formatDate });
    this.filterList.setRecordMapper({ 'filterFromDate': formatDate });
    this.datePickerInitialDate.setValue(formatDate);
  }

  @OnSubmit({ target: 'filter-exams-list' })
  handleSubmitFilter(response: SubmitEvent): void {
    const record = response.record as FilterList;
    const params = {};

    const { patientId } = this.historyService.getCurrentRouteParams();

    this.setParamFilter(params, record.filterExamType?.key, 'filterExamType');
    this.setParamFilter(params, record.filterExamStatus?.key, 'filterExamStatus');
    this.setParamFilter(params, record.filterExamRead?.key, 'filterExamRead');
    this.setParamFilter(params, '6', 'limit');
    this.setParamFilter(params, patientId as string, 'patientId');

    if (record.filterFromDate) {
      const date = record.filterFromDate as string
      this.setParamFilter(params, moment(date, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT), 'filterFromDate');
    } else {
      this.filterList.setRecordMapper({ 'filterFromDate': this.formatDateInitial() });
    }

    this.context.set({
      params: {
        ...params,
        ...record,
      }
    });
    const { filterFromDate } = this.filterList.getRecord() as FilterList;
    this.context.set({ currentDate: filterFromDate});
    this.fetchExams.setParameters(params);
    this.fetchExams.forceFetch();
  }

  setParamFilter(params: FilterParams, value: string, field: string): void {
    if (value != null) {
      params[field] = value;
    }
  }

  @OnClick({ target: ['deck-exams'] })
  async onCardDeckClick(event: ClickEvent): Promise<void> {
    if (event && event.value) {
      const { nrSequencia, nrPrescricao, type, dtProcedimento, cdMedico } = event.value;
      const params = this.context.get('params') as FilterList;
      this.internalNavigation = true;

      const filterExamTypeField = params.filterExamType as FilterItem;
      const filterExamStatusField = params.filterExamStatus as FilterItem;
      const filterExamReadField = params.filterExamRead as FilterItem;
      const filterFromDate: string = moment(params.filterFromDate as string, ISO_DATE_FORMAT).toJSON();

      const filterExamType = filterExamTypeField && filterExamTypeField.key as string;
      const filterExamStatus = filterExamStatusField && filterExamStatusField.key as string;
      const filterExamRead = filterExamReadField && filterExamReadField.key as string;

      const { patientId } = this.historyService.getCurrentRouteParams();

      const routeParams = {
        ...params,
        patientId: patientId,
        dtProcedimento: dtProcedimento as string,
        filterExamType: filterExamType as string || 'ALL',
        filterExamStatus: filterExamStatus as string || 'ALL',
        filterExamRead: filterExamRead as string || 'ALL',
        filterFromDate: filterFromDate as string,
      }

      if (type === 'L') {
        applicationContext.redirect(APPLICATION_ROUTES.EXAM_LAB_DETAILS,{}, {
          searchParams: {
            ...routeParams,
            cdMedico: cdMedico as string,
          }
        });
      } else {
        applicationContext.redirect(APPLICATION_ROUTES.EXAM_DETAILS,{}, {
          searchParams:{
            ...routeParams,
            nrSequencia: nrSequencia as string,
            nrPrescricao: nrPrescricao as string,
            examType: type as string,
            cdMedico: cdMedico as string,
          }
        });
      }
    }
  }

  @OnAfterFetch({ target: 'fetch-exams' })
  onAfterFetchExams(event: FetchEvent): void {
    Audit.filterExecutedLog(this.filterList.getRecord() as Data, (event.data as []).length);
  }

  @OnClear({ target: 'filter-exams-list' })
  onUsersFilterClear(event: ClearEvent<unknown>): void {
    const filter = event.target as unknown as FilterAPI;
    this.autocompleteTypeList.setValue({} as Record<string, Value>);
    this.autocompleteStatusList.setValue({} as Record<string, Value>);
    this.autocompleteReadList.setValue({} as Record<string, Value>);
    filter.setRecordMapper({ 'filterFromDate': this.formatDateInitial() });
    this.datePickerInitialDate.setValue(this.formatDateInitial());
  }

  initialDate(): void {
    HttpService.get(APPLICATION_ENDPOINTS.GET_EXAM_DATE_FROM_FILTER).then(({ body }) => {
      const filterFromDate: string = moment(body as string, ISO_DATE_FORMAT).toJSON();
      this.context.set({ initialValue: filterFromDate });
    });
  }

  formatDateInitial(): string {
    const initialValue = this.context.get('initialValue') as string;
    return initialValue.substr(0, 10);
  }

}
export default ExamResultsController;
